import { useTranslation } from 'react-i18next';
import { useState, useEffect } from 'react';
import api from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { HelpCircle, Search, ChevronDown, ChevronUp } from 'lucide-react';

export default function HelpPage() {
  const { t } = useTranslation();
  const [articles, setArticles] = useState([]);
  const [search, setSearch] = useState('');
  const [expandedId, setExpandedId] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchArticles = async () => {
      try {
        const res = await api.get('/settings/help');
        setArticles(res.data);
      } catch { /* silent */ }
      finally { setLoading(false); }
    };
    fetchArticles();
  }, []);

  const filtered = articles.filter(a =>
    a.title.toLowerCase().includes(search.toLowerCase()) ||
    a.content.toLowerCase().includes(search.toLowerCase()) ||
    a.category.toLowerCase().includes(search.toLowerCase())
  );

  const categories = [...new Set(filtered.map(a => a.category))];

  return (
    <div data-testid="help-page" className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 font-['Barlow'] tracking-tight flex items-center gap-2">
          <HelpCircle className="w-6 h-6 text-[#3399FF]" /> {t('help.title')}
        </h1>
        <p className="text-sm text-gray-500 mt-1">{t('help.subtitle')}</p>
      </div>

      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <Input data-testid="search-help" value={search} onChange={e => setSearch(e.target.value)} placeholder={t('help.searchPlaceholder')} className="pl-10 h-10 bg-white" />
      </div>

      {loading ? (
        <div className="text-center py-12 text-gray-400">{t('common.loading')}</div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-12">
          <HelpCircle className="w-12 h-12 mx-auto mb-3 text-gray-300" />
          <p className="text-gray-500">{t('help.noArticles')}</p>
        </div>
      ) : (
        <div className="space-y-6">
          {categories.map(cat => (
            <div key={cat}>
              <p className="text-xs font-semibold text-[#003366] uppercase tracking-wider mb-3 capitalize">{cat}</p>
              <div className="space-y-2">
                {filtered.filter(a => a.category === cat).map(article => (
                  <Card key={article.id} className="bg-white border-gray-100 hover:border-[#3399FF]/20 transition-colors cursor-pointer" onClick={() => setExpandedId(expandedId === article.id ? null : article.id)}>
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <HelpCircle className="w-4 h-4 text-[#3399FF]" />
                          <h3 className="text-sm font-semibold text-gray-900">{article.title}</h3>
                        </div>
                        {expandedId === article.id ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
                      </div>
                      {expandedId === article.id && (
                        <div className="mt-3 pl-6 space-y-3">
                          <p className="text-sm text-gray-600 whitespace-pre-line">{article.content}</p>
                          {article.video_url && (
                            <div className="rounded-lg overflow-hidden bg-gray-900 aspect-video">
                              <iframe
                                data-testid={`help-video-${article.id}`}
                                src={article.video_url.replace('watch?v=', 'embed/')}
                                title={article.title}
                                className="w-full h-full"
                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                allowFullScreen
                              />
                            </div>
                          )}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
